import { Domain as AuthDomain } from 'auth-common/domain';
import type { PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js/max';
import type { Tagged } from 'type-fest';
import { dayjs } from '../../../shared/utils/date';
import type { AggregationRoot, MergeableEntity } from '../common';
import { SystemMutableEntity as SME } from '../common';
import type { Event as _Event } from '../event';
import { UniqueId } from '../id';

import Project = AuthDomain.Object.Project;

export type UserIdentifier = {
  id: UserIdentifier.Id;
  projectId: Project.Id;
  userId: User.Id;
} & UserIdentifier.Identifier;

export namespace UserIdentifier {
  export type Id = Tagged<UniqueId, 'identifierId'>;
  export namespace Id {
    export const Generate = () => UniqueId.generate() as Id;
    export function FromString(id: string): Id {
      return UniqueId.FromString(id) as Id;
    }
  }

  export type Identifier = Identifier.Phone | Identifier.Email | Identifier.KarteVisitorId | Identifier.KarteUserId | Identifier.ExternalId;

  export namespace Identifier {
    type MalformedNumber = Tagged<string, 'invalidNumber'>;
    export type Phone = {
      type: 'phone';
      value: PhoneNumber | MalformedNumber;
    };
    export type Email = {
      type: 'email';
      value: string;
    };
    export type KarteVisitorId = {
      type: 'karteVisitorId';
      value: string;
    };
    export type KarteUserId = {
      type: 'karteUserId';
      value: string;
    };
    export type ExternalId = {
      type: 'externalId';
      value: string;
    };

    export type Type = Identifier['type'];

    export namespace Construct {
      export type Props = {
        type: string;
        value: string;
      };
    }

    export const Construct = (props: Construct.Props): Identifier => {
      switch (props.type) {
        case 'phone': {
          const normalize = (value: string) => {
            try {
              if (parsePhoneNumber(value, 'JP').isValid()) {
                return parsePhoneNumber(value, 'JP');
              }
              if (parsePhoneNumber(`+${value}`, 'JP').isValid()) {
                return parsePhoneNumber(`+${value}`, 'JP');
              }
              return value as MalformedNumber;
            } catch {
              return value as MalformedNumber;
            }
          };

          const phone = normalize(props.value);
          return {
            type: 'phone',
            value: phone,
          };
        }
        case 'email':
          return {
            type: 'email',
            value: props.value,
          };
        case 'karteVisitorId':
          return {
            type: 'karteVisitorId',
            value: props.value,
          };
        case 'karteUserId':
          return {
            type: 'karteUserId',
            value: props.value,
          };
        case 'externalId':
          return {
            type: 'externalId',
            value: props.value,
          };
        default:
          throw new Error(`Invalid identifier type: ${props.type}`);
      }
    };

    export const Format = (identifier: Identifier): { type: string; value: string } => {
      switch (identifier.type) {
        case 'phone':
          return {
            type: 'phone',
            value: typeof identifier.value === 'string' ? identifier.value : identifier.value.format('E.164'), // E164フォーマットにする
          };
        case 'email':
          return {
            type: 'email',
            value: identifier.value,
          };
        case 'karteVisitorId':
          return {
            type: 'karteVisitorId',
            value: identifier.value,
          };
        case 'karteUserId':
          return {
            type: 'karteUserId',
            value: identifier.value,
          };
        case 'externalId':
          return {
            type: 'externalId',
            value: identifier.value,
          };
        default:
          throw new Error(`Invalid identifier type: ${identifier}`);
      }
    };
  }

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      userId: string;
      type: string;
      value: string;
    };
  }
  export const Construct = (props: Construct.Props): UserIdentifier => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      userId: User.Id.FromString(props.userId),
      ...Identifier.Construct({ type: props.type, value: props.value }),
    };
  };

  export namespace Create {
    export type Props = Omit<Construct.Props, 'id'>;
  }
  export const Create = (props: Create.Props): UserIdentifier => {
    return Construct({ id: Id.Generate(), ...props });
  };

  export const FromIdentifier = (projectId: Project.Id, userId: User.Id, identifier: UserIdentifier.Identifier): UserIdentifier => {
    return {
      id: Id.Generate(),
      projectId,
      userId,
      ...identifier,
    };
  };
}

export type User = AggregationRoot &
  SME &
  MergeableEntity & {
    id: User.Id;
    projectId: Project.Id;
    identifiers: UserIdentifier[];
    metadata?: User.Metadata;
  };

export namespace User {
  export type MetadataValue = {
    latest: number | string | boolean;
    count: number;
    sum?: number;
    min?: number;
    max?: number;
    updatedAt: dayjs;
  };

  export type Metadata = Record<string, MetadataValue>;

  export namespace Metadata {
    export const FromEventMetadata = (eventMetadata: _Event.Metadata): Metadata => {
      const metadata: Metadata = {};
      for (const key in eventMetadata) {
        metadata[key] = {
          latest: eventMetadata[key],
          count: 1,
          sum: typeof eventMetadata[key] === 'number' ? eventMetadata[key] : undefined,
          min: typeof eventMetadata[key] === 'number' ? eventMetadata[key] : undefined,
          max: typeof eventMetadata[key] === 'number' ? eventMetadata[key] : undefined,
          updatedAt: dayjs(),
        };
      }
      return metadata;
    };

    export const Merge = (metadatas: Metadata[]): Metadata => {
      const mergedMetadata: Metadata = {};
      for (const metadata of metadatas) {
        for (const key in metadata) {
          if (!mergedMetadata[key]) {
            mergedMetadata[key] = metadata[key];
          } else {
            // 既存のメタデータを更新
            const existing = mergedMetadata[key];
            const current = metadata[key];

            mergedMetadata[key] = {
              latest: current.latest,
              count: existing.count + current.count,
              sum: existing.sum !== undefined && current.sum !== undefined ? existing.sum + current.sum : undefined,
              min:
                existing.min !== undefined && current.min !== undefined
                  ? Math.min(existing.min, current.min)
                  : (existing.min ?? current.min),
              max:
                existing.max !== undefined && current.max !== undefined
                  ? Math.max(existing.max, current.max)
                  : (existing.max ?? current.max),
              updatedAt: current.updatedAt.isAfter(existing.updatedAt) ? current.updatedAt : existing.updatedAt,
            };
          }
        }
      }
      return mergedMetadata;
    };
  }

  export type Id = Tagged<UniqueId, 'userId'>;
  export namespace Id {
    export const Generate = () => UniqueId.generate() as Id;
    export function FromString(id: string): Id {
      return UniqueId.FromString(id) as Id;
    }
  }

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      identifiers?: { id: string; projectId: string; userId: string; type: string; value: string }[];
      metadata: Metadata;
    } & SME.Props;
  }
  export const Construct = (props: Construct.Props): User => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      identifiers: props.identifiers?.map(UserIdentifier.Construct) ?? [],
      metadata: props.metadata,
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<Construct.Props, 'id' | 'createdAt' | 'updatedAt'>;
  }
  export const Create = (props: Create.Props): User => {
    return Construct({ id: Id.Generate(), ...props, ...SME.Create() });
  };

  export const ApplyEventToUsers = (users: User[], event: Pick<_Event, 'projectId' | 'userIdentifiers' | 'metadata'>): User => {
    const oldestUser = users.sort((a, b) => a.createdAt.valueOf() - b.createdAt.valueOf())[0];

    const mergedUserId = oldestUser?.id ?? Id.Generate();
    const projectId = event.projectId;
    const userIdentifierMap = new Map<string, UserIdentifier>();

    for (const user of users) {
      for (const identifier of user.identifiers) {
        userIdentifierMap.set(`${identifier.type}:${identifier.value}`, identifier);
      }
    }
    for (const identifier of event.userIdentifiers) {
      userIdentifierMap.set(`${identifier.type}:${identifier.value}`, UserIdentifier.FromIdentifier(projectId, mergedUserId, identifier));
    }

    const mutable = oldestUser ? SME.Update({ cur: oldestUser }) : SME.Create();
    return {
      id: mergedUserId,
      projectId,
      identifiers: Array.from(userIdentifierMap.values()),
      metadata: Metadata.Merge([...users.map((user) => user.metadata ?? {}), Metadata.FromEventMetadata(event.metadata ?? {})]),
      createdAt: mutable.createdAt,
      updatedAt: mutable.updatedAt,
    };
  };
}
